/**
 * @voltxampere/persistence — Command palette recent ring (Sprint 22 C4)
 *
 * Stores up to {@link DEFAULT_COMMAND_RECENTS_SIZE} most-recently
 * executed command ids, newest-first. The store holds a single
 * sentinel row whose `ids` field is the ordered list — atomic
 * read/write semantics, no cursor walks.
 *
 * Design decisions (Sprint 22 D-22-3 closure):
 *
 *  - Persisted in IndexedDB rather than localStorage so the data
 *    survives the user's storage quota cleanups, integrates with
 *    the AYARLAR "Veri" tab "Tümünü temizle" button, and shares
 *    the same auto-save lifecycle as circuits.
 *  - Ring size is hard-coded at 8 — matches the palette's
 *    Suggested-section limit, so Recent + Suggested together cap
 *    at 16 rows (the empty-state list always fits one viewport).
 *
 * Public API:
 *
 *  - getCommandRecents()    — ordered ids, newest-first, [] if none
 *  - pushCommandRecent(id)  — record an execution; deduplicates so
 *                             the same id never appears twice
 *  - clearCommandRecents()  — wipe the row (called from the
 *                             AYARLAR "Veri" tab clear-all button)
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { openDb, requestToPromise, transactionDone } from "./db.js";
import { COMMAND_RECENTS_SENTINEL_KEY, DEFAULT_COMMAND_RECENTS_SIZE, STORE_COMMAND_RECENTS, } from "./types.js";
/**
 * Read the most-recent command ids in execution order
 * (newest-first). Returns `[]` when the store has no row yet
 * (fresh install, post-clear, etc.).
 */
export async function getCommandRecents() {
    const db = await openDb();
    const tx = db.transaction(STORE_COMMAND_RECENTS, "readonly");
    const store = tx.objectStore(STORE_COMMAND_RECENTS);
    const raw = await requestToPromise(store.get(COMMAND_RECENTS_SENTINEL_KEY));
    await transactionDone(tx);
    if (raw === undefined || raw === null)
        return [];
    // IDB returns `unknown` shapes; we always wrote
    // `CommandRecentsRecord`, but a future migration could leave a
    // shape mismatch. Validate before trusting.
    if (typeof raw !== "object")
        return [];
    const ids = raw.ids;
    if (!Array.isArray(ids))
        return [];
    return ids.filter((entry) => typeof entry === "string");
}
/**
 * Record a command execution. The `id` is moved to the front of
 * the ring (slot 0); any prior occurrence is removed before the
 * new head is written so duplicates never accumulate. The list
 * is trimmed to {@link DEFAULT_COMMAND_RECENTS_SIZE}.
 */
export async function pushCommandRecent(id, now = Date.now()) {
    const db = await openDb();
    const tx = db.transaction(STORE_COMMAND_RECENTS, "readwrite");
    const store = tx.objectStore(STORE_COMMAND_RECENTS);
    const existing = await requestToPromise(store.get(COMMAND_RECENTS_SENTINEL_KEY));
    const previousIds = existing !== undefined && existing !== null && typeof existing === "object"
        ? (() => {
            const ids = existing.ids;
            if (!Array.isArray(ids))
                return [];
            return ids.filter((entry) => typeof entry === "string");
        })()
        : [];
    // Dedupe + prepend + trim.
    const without = previousIds.filter((existingId) => existingId !== id);
    const next = [id, ...without].slice(0, DEFAULT_COMMAND_RECENTS_SIZE);
    const record = {
        key: COMMAND_RECENTS_SENTINEL_KEY,
        ids: next,
        updatedAt: now,
    };
    await requestToPromise(store.put(record));
    await transactionDone(tx);
    return next;
}
/** Clear the recent ring. Used by AYARLAR "Tümünü temizle". */
export async function clearCommandRecents() {
    const db = await openDb();
    const tx = db.transaction(STORE_COMMAND_RECENTS, "readwrite");
    const store = tx.objectStore(STORE_COMMAND_RECENTS);
    await requestToPromise(store.delete(COMMAND_RECENTS_SENTINEL_KEY));
    await transactionDone(tx);
}
//# sourceMappingURL=command-recents-store.js.map